import { Fragment, useState } from "react";
import Title from "../typography/Title";
import Description from "../typography/Description";
import {
  VideoCamera,
  Phone,
  FileText,
  Brain,
  Code,
  Coins,
  Envelope,
  MagnifyingGlass,
} from "@phosphor-icons/react/dist/ssr";

// Sourcing rows come before screening rows — that's the order the hiring
// pipeline actually runs in (find and enrich profiles, then screen them).
const SECTION_LABELS: Record<string, { label: string; icon: typeof MagnifyingGlass }> = {
  sourcing: { label: "AI Talent Sourcing Agent", icon: MagnifyingGlass },
  screening: { label: "AI Screening Agent", icon: VideoCamera },
};

const modules = [
  {
    name: "Email Enrichment",
    icon: Envelope,
    credits: 1,
    per: "profile",
    note: "Reveals a verified work/personal email for a sourced profile.",
    category: "sourcing",
  },
  {
    name: "Phone Enrichment",
    icon: Phone,
    credits: 2,
    per: "profile",
    note: "Reveals a verified phone number for a sourced profile.",
    category: "sourcing",
  },
  {
    name: "AI Video Interviews",
    icon: VideoCamera,
    credits: 25,
    per: "interview",
    note: "Windows, Mac, Android & iOS",
    category: "screening",
  },
  {
    name: "AI Phone Screening",
    icon: Phone,
    credits: 5,
    per: "interview",
    note: "Only connected calls more than 60 seconds use credits. Voicemails not counted.",
    category: "screening",
  },
  {
    name: "AI Resume Parsing",
    icon: FileText,
    credits: 1,
    per: "CV",
    note: "Free with Enterprise plans.",
    category: "screening",
  },
  {
    name: "Aptitude & Psychometric Tests",
    icon: Brain,
    credits: 10,
    per: "assessment",
    note: "MCQ format. Use ours or upload your own.",
    category: "screening",
  },
  {
    name: "AI Coding Assessments",
    icon: Code,
    credits: 40,
    per: "assessment",
    note: "30–90 min, 70+ skills, live IDE.",
    category: "screening",
  },
];

// Reference plans this table can show credit usage against. Team is the
// recommended plan (₹15,000 / 1,500 credits = ₹10/credit) and is the
// default; Starter is ₹7,500 / 500 credits = ₹15/credit.
const PLAN_OPTIONS = {
  team: { name: "Team", credits: 1500, pricePerCreditInr: 10.0 },
  starter: { name: "Starter", credits: 500, pricePerCreditInr: 15.0 },
} as const;
type PlanKey = keyof typeof PLAN_OPTIONS;

// Precomputed once — modules are already grouped by category in source
// order, so each group's row count is just how many share that category.
const categoryCounts = modules.reduce<Record<string, number>>((acc, mod) => {
  acc[mod.category] = (acc[mod.category] || 0) + 1;
  return acc;
}, {});

const TokenComparison = () => {
  const [planKey, setPlanKey] = useState<PlanKey>("team");
  const selectedPlan = PLAN_OPTIONS[planKey];
  const sym = "₹";
  const pricePerCredit = selectedPlan.pricePerCreditInr;
  const taxLabel = "(INR, excl. GST)";
  const formatPrice = (n: number) => n.toFixed(0);

  return (
    <section className="relative z-10 px-4 py-16 sm:px-6 lg:px-10 xl:px-16">
      <Title size="lg">AI-Credit Usage Table</Title>
      <Description className="mx-auto mt-4 max-w-2xl">
        Credits are only consumed after reaching completion thresholds, not on
        invitation.
      </Description>

      <div className="mx-auto mt-6 flex items-center justify-center gap-2">
        <label htmlFor="token-plan-select" className="text-sm text-gray-300">
          Show pricing for:
        </label>
        <select
          id="token-plan-select"
          value={planKey}
          onChange={(e) => setPlanKey(e.target.value as PlanKey)}
          className="rounded-lg border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-50 transition-all focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
        >
          {(Object.keys(PLAN_OPTIONS) as PlanKey[]).map((key) => (
            <option key={key} value={key}>
              {PLAN_OPTIONS[key].name} plan
            </option>
          ))}
        </select>
      </div>

      <div className="mx-auto mt-8 max-w-4xl">
        {/* Desktop Table */}
        <div className="hidden overflow-hidden rounded-2xl border border-white/[0.08] bg-gray-600 md:block">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/[0.08] bg-gray-650">
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                  Agent
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                  Tasks by AI-Agents
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                  <div className="flex items-center gap-2">
                    <Coins size={16} weight="fill" className="text-amber-400" />
                    Credits / Task
                  </div>
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                  <span className="text-white">{selectedPlan.name}</span>
                  <br />
                  <span className="text-xs font-normal text-gray-350">
                    {selectedPlan.credits.toLocaleString("en-IN")} credits
                  </span>
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                  Price / Task
                  <br />
                  <span className="text-xs font-normal text-gray-350">{taxLabel}</span>
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                  How it works
                </th>
              </tr>
            </thead>
            <tbody>
              {modules.map((mod, idx) => {
                const Icon = mod.icon;
                const units = Math.floor(selectedPlan.credits / mod.credits);
                const isNewSection =
                  idx === 0 || modules[idx - 1].category !== mod.category;
                const section = SECTION_LABELS[mod.category];
                const SectionIcon = section.icon;
                // Extra-thick top border marking the boundary between the
                // sourcing and screening groups (not on the very first row).
                const sectionDivider =
                  isNewSection && idx !== 0 ? "border-t-2 border-t-teal-500/30" : "";
                return (
                  <Fragment key={mod.name}>
                    <tr
                      className={
                        idx % 2 === 0 ? "bg-gray-600" : "bg-gray-650"
                      }
                    >
                    {isNewSection && (
                      <td
                        rowSpan={categoryCounts[mod.category]}
                        className={`border-r border-white/[0.08] bg-gray-600 px-3 py-4 text-center align-middle ${sectionDivider}`}
                      >
                        <div className="flex flex-col items-center gap-1.5">
                          <SectionIcon size={16} weight="bold" className="text-teal-400" />
                          <p className="text-xs font-semibold uppercase leading-tight tracking-wider text-teal-400">
                            {section.label}
                          </p>
                        </div>
                      </td>
                    )}
                    <td className={`px-4 py-4 ${sectionDivider}`}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.06]">
                          <Icon
                            size={18}
                            weight="duotone"
                            className="text-cyan-400"
                          />
                        </div>
                        <span className="text-sm font-medium text-white">
                          {mod.name}
                        </span>
                      </div>
                    </td>
                    <td className={`px-4 py-4 text-left ${sectionDivider}`}>
                      <span className="text-lg font-bold text-amber-400">
                        {mod.credits.toLocaleString("en-IN")}
                      </span>
                      <span className="ml-1 text-xs text-gray-300">
                        / {mod.per}
                      </span>
                    </td>
                    <td className={`px-4 py-4 text-left ${sectionDivider}`}>
                      <span className="text-base font-semibold text-white">
                        {units.toLocaleString("en-IN")}
                      </span>
                      <span className="ml-1 text-xs text-gray-350">
                        {mod.per === "CV" ? "CVs" : `${mod.per}s`}
                      </span>
                    </td>
                    <td className={`px-4 py-4 text-left ${sectionDivider}`}>
                      <span className="text-base font-semibold text-green-400">
                        {sym}{formatPrice(mod.credits * pricePerCredit)}
                      </span>
                      <span className="ml-1 text-xs text-gray-350">
                        / {mod.per}
                      </span>
                    </td>
                    <td className={`px-4 py-4 text-left ${sectionDivider}`}>
                      <span className="text-xs leading-relaxed text-white">
                        {mod.note}
                      </span>
                    </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="flex flex-col gap-4 md:hidden">
          {modules.map((mod, idx) => {
            const Icon = mod.icon;
            const units = Math.floor(selectedPlan.credits / mod.credits);
            const isNewSection =
              idx === 0 || modules[idx - 1].category !== mod.category;
            const section = SECTION_LABELS[mod.category];
            const SectionIcon = section.icon;
            return (
              <Fragment key={mod.name}>
                {isNewSection && (
                  <div className="mt-6 flex items-center gap-1.5 border-t border-teal-500/30 pt-6 first:mt-0 first:border-t-0 first:pt-0">
                    <SectionIcon size={14} weight="bold" className="text-teal-400" />
                    <p className="text-xs font-semibold uppercase tracking-wider text-teal-400">
                      {section.label}
                    </p>
                  </div>
                )}
                <div
                  className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4"
                >
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/[0.06]">
                    <Icon
                      size={18}
                      weight="duotone"
                      className="text-cyan-400"
                    />
                  </div>
                  <span className="text-sm font-semibold text-white">
                    {mod.name}
                  </span>
                </div>
                <p className="mb-3 text-xs leading-relaxed text-gray-300">{mod.note}</p>
                <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-400/[0.08] px-3 py-2">
                  <Coins size={16} weight="fill" className="text-amber-400" />
                  <span className="text-sm font-bold text-amber-400">
                    {mod.credits.toLocaleString("en-IN")} credits
                  </span>
                  <span className="text-xs text-gray-300">/ {mod.per}</span>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 rounded-lg bg-white/[0.04] px-3 py-2 text-center">
                    <p className="text-xs text-gray-300">{selectedPlan.name}</p>
                    <p className="text-sm font-semibold text-white">
                      {units.toLocaleString("en-IN")}{" "}
                      <span className="text-xs font-normal text-gray-350">
                        {mod.per === "CV" ? "CVs" : `${mod.per}s`}
                      </span>
                    </p>
                  </div>
                  <div className="flex-1 rounded-lg bg-white/[0.04] px-3 py-2 text-center">
                    <p className="text-xs text-gray-300">Price / {mod.per}</p>
                    <p className="text-sm font-semibold text-green-400">
                      {sym}{formatPrice(mod.credits * pricePerCredit)}
                    </p>
                  </div>
                </div>
                </div>
              </Fragment>
            );
          })}
        </div>

        <p className="mt-6 text-center text-xs text-gray-300">
          * Numbers shown are maximum units per AI agent if all credits are
          allocated to a single agent. Mix and match agents within your credit
          budget.
        </p>
      </div>
    </section>
  );
};

export default TokenComparison;
